import {useState} from 'react'
import {Link as RouterLink, useParams} from 'react-router-dom'
import {Trans, useTranslation} from 'react-i18next'
import {unsubscribe} from '../../common/api/generated/endpoints/newsletter/newsletter'
import {reportError} from '../../common/errors/reportError'
import './UnsubscribePage.css'

type ViewState = 'idle' | 'pending' | 'success' | 'invalid' | 'error'

const TRANS_COMPONENTS = {strong: <strong />, contact: <RouterLink to="/contact" />}

const UnsubscribePage = () => {
  const {t} = useTranslation()
  const {token} = useParams<{token: string}>()
  const [view, setView] = useState<ViewState>('idle')
  const [email, setEmail] = useState<string | null>(null)

  const handleConfirm = async () => {
    if (!token) {
      setView('invalid')
      return
    }
    setView('pending')
    try {
      const res = await unsubscribe({token})
      if (res.data.success) {
        setEmail(res.data.email ?? null)
        setView('success')
      } else {
        setView('invalid')
      }
    } catch (err) {
      reportError(err, 'UnsubscribePage')
      setView('error')
    }
  }

  return (
    <div className="unsubscribe-root">
      <div className="us-grid" aria-hidden />

      <main className="unsubscribe-page">
        <div className="us-card">
          {view === 'idle' && (
            <>
              <h1 className="us-title">{t('unsubscribe.title')}</h1>
              <p className="us-lede">{t('unsubscribe.lede')}</p>
              <button type="button" className="us-btn" onClick={handleConfirm}>
                {t('unsubscribe.confirmBtn')}
              </button>
            </>
          )}

          {view === 'pending' && (
            <>
              <h1 className="us-title">{t('unsubscribe.title')}</h1>
              <p className="us-lede">{t('unsubscribe.pending')}</p>
            </>
          )}

          {view === 'success' && (
            <div role="status" aria-live="polite">
              <div className="us-icon us-icon-success" aria-hidden>
                ✓
              </div>
              <h1 className="us-title">{t('unsubscribe.successTitle')}</h1>
              <p className="us-lede">
                {email ? (
                  <Trans i18nKey="unsubscribe.successBody" values={{email}} components={TRANS_COMPONENTS} />
                ) : (
                  t('unsubscribe.successBodyNoEmail')
                )}
              </p>
              <RouterLink to="/" className="us-link">
                {t('unsubscribe.backHome')}
              </RouterLink>
            </div>
          )}

          {view === 'invalid' && (
            <>
              <div className="us-icon us-icon-warn" aria-hidden>
                !
              </div>
              <h1 className="us-title">{t('unsubscribe.invalidTitle')}</h1>
              <p className="us-lede">
                <Trans i18nKey="unsubscribe.invalidBody" components={TRANS_COMPONENTS} />
              </p>
              <RouterLink to="/" className="us-link">
                {t('unsubscribe.backHome')}
              </RouterLink>
            </>
          )}

          {view === 'error' && (
            <>
              <div className="us-icon us-icon-warn" aria-hidden>
                !
              </div>
              <h1 className="us-title">{t('unsubscribe.errorTitle')}</h1>
              <p className="us-lede">
                <Trans i18nKey="unsubscribe.errorBody" components={TRANS_COMPONENTS} />
              </p>
              <button type="button" className="us-btn" onClick={handleConfirm}>
                {t('unsubscribe.confirmBtn')}
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  )
}

export default UnsubscribePage
